/*!
=========================================================
* Argon Dashboard PRO React - Chart.js v4
=========================================================
*/
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

// Random function for demo
const randomScalingFactor = () => Math.round(Math.random() * 100);

// Rounded bar extension
Chart.defaults.borderRadius = 6;

// Colors & fonts
const mode = "light";
const fonts = { base: "Open Sans" };
const colors = {
  gray: {
    100: "#f6f9fc",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#8898aa",
    700: "#525f7f",
    800: "#32325d",
    900: "#212529",
  },
  theme: {
    default: "#172b4d",
    primary: "#5e72e4",
    secondary: "#f4f5f7",
    info: "#11cdef",
    success: "#2dce89",
    danger: "#f5365c",
    warning: "#fb6340",
  },
  black: "#12263F",
  white: "#FFFFFF",
  transparent: "transparent",
};

// Global chart options
const chartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
      position: "bottom",
      labels: { usePointStyle: true, padding: 16 },
    },
    tooltip: { mode: "index", intersect: false },
  },
  layout: { padding: 0 },
  elements: {
    point: { radius: 0, backgroundColor: colors.theme.primary },
    line: {
      tension: 0.4,
      borderWidth: 4,
      borderColor: colors.theme.primary,
      backgroundColor: colors.transparent,
      borderCapStyle: "round",
    },
    rectangle: { backgroundColor: colors.theme.warning, borderRadius: 6 },
    arc: {
      backgroundColor: colors.theme.primary,
      borderColor: mode === "dark" ? colors.gray[800] : colors.white,
      borderWidth: 4,
    },
  },
  scales: {
    x: {
      grid: {
        drawBorder: false,
        drawOnChartArea: false,
        drawTicks: false,
        color: colors.gray[300],
      },
      ticks: { padding: 20 },
    },
    y: {
      beginAtZero: true,
      grid: {
        borderDash: [2],
        drawBorder: false,
        color: colors.gray[300],
        zeroLineColor: colors.gray[300],
      },
      ticks: {
        padding: 10,
        callback: (value) => (value % 10 === 0 ? value : undefined),
      },
    },
  },
});

// Parse options helper
const parseOptions = (parent, options) => {
  Object.keys(options).forEach((item) => {
    if (typeof options[item] !== "object") parent[item] = options[item];
    else parseOptions(parent[item], options[item]);
  });
};

// Chart examples
const chartExample1 = {
  options: {
    responsive: true,
    maintainAspectRatio: false,

    scales: {
      y: {
        grid: {
          color: colors.gray[700],
          zeroLineColor: colors.gray[700],
        },
        ticks: {
          callback: function (value) {
            if (!(value % 10)) {
              return "$" + value + "k";
            }
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },

    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            return `${label ? label + ": " : ""}$${value}k`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
  },

  data1: {
    labels: ["May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    datasets: [
      {
        label: "Performance",
        data: [0, 20, 10, 30, 15, 40, 20, 60],
        borderColor: colors.theme.info,
        backgroundColor: colors.transparent,
        fill: false,
        tension: 0.4,
      },
    ],
  },

  data2: {
    labels: ["May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    datasets: [
      {
        label: "Performance",
        data: [0, 20, 5, 25, 10, 30, 15, 40],
        borderColor: colors.theme.info,
        backgroundColor: colors.transparent,
        fill: false,
        tension: 0.4,
      },
    ],
  },
};

const chartExample2 = {
  options: {
    responsive: true,

    plugins: {
      legend: { display: false },

      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;

            if (context.chart.data.datasets.length > 1) {
              return `${label}: ${value}`;
            }
            return `${value}`;
          },
        },
      },
    },

    scales: {
      y: {
        grid: {
          color: colors.gray[200],
          zeroLineColor: colors.gray[200],
        },
        ticks: {
          callback: function (value) {
            if (!(value % 10)) {
              return value;
            }
          },
        },
      },

      x: {
        grid: {
          drawBorder: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
      },
    },
  },

  data: {
    labels: ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],

    datasets: [
      {
        label: "Sales",
        data: [25, 20, 30, 22, 17, 29],
        backgroundColor: colors.theme.primary,
        maxBarThickness: 10,
      },
    ],
  },
};

export {
  chartOptions,
  parseOptions,
  chartExample1,
  chartExample2,
  randomScalingFactor,
  colors,
};
